import type { BlockField } from '@/blocks/domain/types';

/**
 * D-28 (LLAMS-610 DB-12): the Bill to / Job site block carries two party groups encoded in its field
 * ids (`cust-pf-*` = Prepared For, `cust-pb-*` = Prepared By). A *bound* party field (name, contact,
 * address) belongs to its group and may not be dropped under the other party's heading; headings and
 * every unbound element (Label + value, Text, Image, ...) move freely.
 */
const PARTY_ID = /^cust-(p[fb])-(.+)$/;

const DEFAULT_GROUP_LABELS: Record<string, string> = { pf: 'Prepared For', pb: 'Prepared By' };

export interface PartyMoveRejection {
  fieldId: string;
  groupKey: string;
  groupLabel: string;
}

function partyOf(field: BlockField): { groupKey: string; suffix: string } | null {
  const match = PARTY_ID.exec(field.id);
  if (!match?.[1] || !match[2]) return null;
  return { groupKey: match[1], suffix: match[2] };
}

function isBoundParty(field: BlockField): boolean {
  const party = partyOf(field);
  return party !== null && party.suffix !== 'heading' && field.style !== 'heading';
}

function groupLabelOf(fields: BlockField[], groupKey: string): string {
  const heading = fields.find((f) => f.id === `cust-${groupKey}-heading`);
  const text = heading?.value.trim();
  return text || (DEFAULT_GROUP_LABELS[groupKey] ?? groupKey);
}

/** The party group whose heading most recently precedes `index`, or `null` above every heading. */
function zoneAt(fields: BlockField[], index: number): string | null {
  for (let i = index - 1; i >= 0; i -= 1) {
    const candidate = fields[i];
    if (!candidate) continue;
    const party = partyOf(candidate);
    if (party?.suffix === 'heading') return party.groupKey;
  }
  return null;
}

/**
 * Returns the rejection when moving `fromIndex` -> `toIndex` would put a bound party field under the
 * other party's heading; `null` when the move is allowed (including every unbound element).
 */
export function rejectPartyMove(fields: BlockField[], fromIndex: number, toIndex: number): PartyMoveRejection | null {
  const moved = fields[fromIndex];
  if (!moved || !isBoundParty(moved) || fromIndex === toIndex) return null;
  const party = partyOf(moved);
  if (!party) return null;

  const next = [...fields];
  next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);

  const zone = zoneAt(next, toIndex);
  if (zone === party.groupKey) return null;
  // Above every heading is outside every group (D-28) - unless the field's own heading is gone, in
  // which case there is no group left to protect.
  if (zone === null && !fields.some((f) => f.id === `cust-${party.groupKey}-heading`)) return null;
  return { fieldId: moved.id, groupKey: party.groupKey, groupLabel: groupLabelOf(fields, party.groupKey) };
}

/** `true` for the `cust-pf-heading` / `cust-pb-heading` rows that open a party group. */
export function isPartyHeading(field: BlockField): boolean {
  return partyOf(field)?.suffix === 'heading';
}

/** `true` for a bound party row (name, contact, address): it belongs to its own group's column (D-28). */
export function isBoundPartyField(field: BlockField): boolean {
  return isBoundParty(field);
}

/** The party group key (`pf` / `pb`) a party row or heading belongs to, or `null` for every other element. */
export function partyGroupKeyOf(field: BlockField): string | null {
  return partyOf(field)?.groupKey ?? null;
}

/** The party group label a field belongs to, used as the duplicate-name suffix; `null` for other fields. */
export function partyGroupLabelOf(fields: BlockField[], field: BlockField): string | null {
  const party = partyOf(field);
  return party ? groupLabelOf(fields, party.groupKey) : null;
}

/**
 * The party group label the slot at `index` sits under, or `null` above every heading / outside the
 * party block.
 */
export function partyZoneLabelAt(fields: BlockField[], index: number): string | null {
  if (!fields.some((f) => PARTY_ID.test(f.id))) return null;
  const zone = zoneAt(fields, index);
  return zone === null ? null : groupLabelOf(fields, zone);
}
